import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Loader2, Timer } from 'lucide-react';

export const EXTRA_TIME_OPTIONS = [
  ['none', 'No extra time'],
  ['25', '+25% time'],
  ['50', '+50% time'],
  ['100', 'Double time (+100%)'],
  ['unlimited', 'Unlimited time'],
];

// Accessibility accommodation: extra time on all timed quizzes for one learner
export const ExtraTimeDialog = ({ user, open, onOpenChange }) => {
  const { api } = useAuth();
  const [value, setValue] = useState('none');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !user?.id) return;
    setLoading(true);
    api.get(`/users/${user.id}/extra-time`)
      .then(r => setValue(r.data?.extra_time || 'none'))
      .catch(() => setValue('none'))
      .finally(() => setLoading(false));
  }, [open, user?.id, api]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/users/${user.id}/extra-time`, { extra_time: value });
      toast.success('Extra time updated');
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not update extra time');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="extra-time-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Timer className="h-5 w-5 text-[#20acf6]" /> Extra time</DialogTitle>
          <DialogDescription>
            For {user?.first_name} {user?.last_name}. Applies to every quiz with a time limit, such as formal final assessments. Quizzes without a time limit are unaffected.
          </DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-[#20acf6]" /></div>
        ) : (
          <div className="space-y-2" role="radiogroup">
            {EXTRA_TIME_OPTIONS.map(([v, label]) => (
              <label key={v} className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer ${value === v ? 'border-[#20acf6] bg-blue-50' : 'border-slate-200 hover:bg-slate-50'}`}>
                <input type="radio" name="extra-time" checked={value === v} onChange={() => setValue(v)} className="accent-[#20acf6]" data-testid={`extra-time-option-${v}`} />
                <span className="text-sm">{label}</span>
              </label>
            ))}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={saving || loading} className="bg-[#20acf6] hover:bg-[#3bb8f7]" data-testid="extra-time-save-btn">
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
